import { ReactElement, useMemo } from "react"
import * as XLSX from "xlsx"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileExcel } from "@fortawesome/free-solid-svg-icons"

import { IReportObs, ObservationStatus } from "../../../../interfaces/markets"
import { formatDate } from "../../../../utils/helpers"

type Props = {
  data: IReportObs[]
}

type SheetData = Record<string, IReportObs[]>

const BASE_COLUMNS = [
  "commodityPriceDate",
  "marketName",
  "commodityName",
  "priceTypeName",
  "commodityUnitName",
  "currencyName",
  "commodityPriceValue",
  "commodityPriceSourceName",
]

const OUTLIER_COLUMNS = ["MoM", "YoY", "lowerBound", "upperBound"]

const COLUMN_HEADERS: Record<string, string> = {
  commodityPriceDate: "Price Date",
  marketName: "Market",
  commodityName: "Commodity",
  priceTypeName: "Price Type",
  commodityUnitName: "Unit",
  currencyName: "Currency",
  commodityPriceValue: "Price Value",
  commodityPriceSourceName: "Source",
  MoM: "MoM (%)",
  YoY: "YoY (%)",
  lowerBound: "Lower Bound",
  upperBound: "Upper Bound",
}

const DownloadExcel = ({ data }: Props): ReactElement => {
  const hasData = data?.length > 0

  const groupedSheets = useMemo((): SheetData => {
    return data.reduce<SheetData>((acc, item) => {
      const sheetName = ObservationStatus[item.observationStatusId]
      if (!sheetName) return acc

      if (!acc[sheetName]) {
        acc[sheetName] = []
      }
      acc[sheetName].push(item)
      return acc
    }, {})
  }, [data])

  const formatItem = (item: IReportObs): Record<string, string | number> => {
    const columnsToUse: string[] =
      item.observationStatusId === ObservationStatus.VALID ? [...BASE_COLUMNS, ...OUTLIER_COLUMNS] : BASE_COLUMNS

    return columnsToUse.reduce<Record<string, string | number>>((acc, col) => {
      const headerName = COLUMN_HEADERS[col] || col
      let value = item[col as keyof IReportObs]

      if (col === "commodityPriceDate" && typeof value === "string") {
        value = formatDate(value, "DD-MMM-YYYY")
      }
      if (col === "MoM" || (col === "YoY" && typeof value === "number")) {
        value = value ? Number((value as number).toFixed(1).replace(/\.0$/, "")) : ""
      }

      acc[headerName] = (value as string | number) ?? ""
      return acc
    }, {})
  }

  const handleDownload = (): void => {
    if (!hasData) return

    const workbook = XLSX.utils.book_new()

    Object.entries(groupedSheets).forEach(([sheetName, items]) => {
      const formattedItems = items.map(formatItem)
      const worksheet = XLSX.utils.json_to_sheet(formattedItems)
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    })

    const reportId = data[0]?.reportId || "unknown"
    const fileName = `validation-data-report-${reportId}.xlsx`

    XLSX.writeFile(workbook, fileName)
  }

  return (
    <FontAwesomeIcon
      icon={faFileExcel}
      onClick={handleDownload}
      color={hasData ? "var(--interactive-01)" : "#aaa"}
      size="lg"
      className={`ml-1.5 ${hasData ? "cursor-pointer" : "opacity-50"}`}
      title="Download Validation Report"
      style={{ pointerEvents: hasData ? "auto" : "none" }}
    />
  )
}

export default DownloadExcel
